import * as THREE from 'three'
import { mulberry32 } from '../prng.ts'

// Authored tube network over the MIX region (z ≈ 16–58). Three registers per
// FOUNDATION §6.4: 0 industrial duct, 1 glass conduit, 2 light tube.
// Messiness is the brief: a cross-duct that doesn't line up, diagonal offenders,
// intakes dropping to desk level, and one light run that leaves the canopy to
// weave between desks — which is where the floor spill actually gets seen.
export interface TubeDef {
  curve: THREE.CatmullRomCurve3
  register: 0 | 1 | 2
  radius: number
}

const rand = mulberry32(777)
const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z)
const j = (a: number) => (rand() * 2 - 1) * a

export const TUBES: TubeDef[] = []

// ── ducts: fat, badly routed, air-conditioning-like ──────────────────────
// Runs push past the MIX region at both ends (V1's one-point-perspective read):
// no duct terminates where the camera can see a clean cut — the ends taper and
// dissolve into the air (see Tubes.tsx taper pass).
for (const x0 of [-14, -4, 8]) {
  TUBES.push({
    register: 0,
    radius: 0.3 + rand() * 0.08,
    curve: new THREE.CatmullRomCurve3([
      V(x0 + j(2), 6.7 + j(0.4), 15), V(x0 + j(2.5), 6.0 + j(0.6), 26),
      V(x0 + j(3), 6.6 + j(0.5), 36), V(x0 + j(2.5), 6.1 + j(0.6), 46),
      V(x0 + j(2), 6.5 + j(0.4), 56), V(x0 + j(2), 6.8 + j(0.4), 63),
    ]),
  })
}
// the cross-duct — junctions that visibly do not line up
TUBES.push({
  register: 0,
  radius: 0.26,
  curve: new THREE.CatmullRomCurve3([
    V(-38, 5.9, 32), V(-26, 5.5, 33), V(-10, 6.1, 34.5), V(4, 5.6, 33.5), V(20, 6.3, 35), V(30, 5.8, 34), V(42, 6.2, 35),
  ]),
})
// two diagonal offenders
TUBES.push({
  register: 0,
  radius: 0.2,
  curve: new THREE.CatmullRomCurve3([
    V(-32, 4.9, 12), V(-24, 5.2, 20), V(-14, 6.4, 30), V(-2, 5.7, 42), V(8, 6.5, 52), V(16, 7.0, 60),
  ]),
})
TUBES.push({
  register: 0,
  radius: 0.22,
  curve: new THREE.CatmullRomCurve3([
    V(30, 5.2, 10), V(22, 5.4, 18), V(12, 6.2, 28), V(2, 5.8, 40), V(-8, 6.6, 54), V(-15, 7.0, 62),
  ]),
})

// ── glass conduits: intakes dropped toward the desk field, glyphs rushing inside ──
for (let i = 0; i < 6; i++) {
  const x0 = -22 + i * 8.5 + j(2)
  TUBES.push({
    register: 1,
    radius: 0.16,
    curve: new THREE.CatmullRomCurve3([
      V(x0, 1.8 + rand() * 0.5, 19 + rand() * 9), // mouth, low over the desks
      V(x0 * 0.95 + j(1.5), 3.6 + j(0.8), 27 + rand() * 4),
      V(x0 * 0.8 + j(3), 5.9 + j(0.7), 33 + rand() * 5),
      V(x0 * 0.6 + j(4), 6.3 + j(0.6), 42 + rand() * 5),
      V(x0 * 0.35 + j(3), 6.0 + j(0.7), 51 + rand() * 5),
    ]),
  })
}

// ── light tubes: threading the canopy, plus the dropped run ───────────────
for (let i = 0; i < 4; i++) {
  const x0 = -18 + i * 11 + j(2)
  TUBES.push({
    register: 2,
    radius: 0.085,
    curve: new THREE.CatmullRomCurve3([
      V(x0, 6.6 + j(0.4), 16), V(x0 + j(3), 6.3 + j(0.5), 28),
      V(x0 + j(4), 6.7 + j(0.4), 40), V(x0 + j(3), 6.4 + j(0.5), 54), V(x0 + j(2), 6.8 + j(0.3), 62),
    ]),
  })
}
// corridor seam lights — two thin runs converging on the aperture rim
// (ported from V1's library passage: the leading lines that make the white
// circle read as a tunnel mouth, not a sun). They taper INTO the hole.
for (const s of [-1, 1]) {
  TUBES.push({
    register: 2,
    radius: 0.06,
    curve: new THREE.CatmullRomCurve3([
      V(s * 3.5, 7.1, 87), V(s * 3.1, 6.8, 96), V(s * 2.7, 6.4, 105), V(s * 2.4, 6.13, 113.2),
    ]),
  })
}
// the dropped run — weaves low between desks right by the camera path;
// this is where FOUNDATION §4.2 mechanism 1 (spill on white) is actually seen
TUBES.push({
  register: 2,
  radius: 0.09,
  curve: new THREE.CatmullRomCurve3([
    V(-5.5, 1.7, 21), V(-2.5, 1.3, 24.5), V(1.5, 1.9, 27), V(4.5, 1.4, 30),
    V(5.5, 2.4, 33.5), V(3.5, 4.4, 36.5), V(1.5, 6.2, 40),
  ]),
})

// ── zip-tie rings on the ducts — the mess is the brief ────────────────────
export const RINGS: { m: THREE.Matrix4 }[] = []
{
  const up = new THREE.Vector3(0, 1, 0)
  for (const t of TUBES.filter((t) => t.register === 0)) {
    for (let i = 0; i < 4; i++) {
      // stay clear of the tapered ends (Tubes.tsx shrinks u < 0.22 / u > 0.78)
      const u = 0.26 + rand() * 0.48
      const p = t.curve.getPoint(u)
      const tan = t.curve.getTangent(u)
      const m = new THREE.Matrix4().lookAt(new THREE.Vector3(), tan, up)
      m.multiply(new THREE.Matrix4().makeScale(t.radius, t.radius, t.radius))
      m.setPosition(p)
      RINGS.push({ m })
    }
  }
}

// Glyph flow paths (flowMode 1): the 6 glass conduits + 2 canopy light tubes,
// each approximated by a cubic Bezier through 4 samples (stateless shader eval).
export const GLYPH_PATHS: THREE.Vector3[][] = [
  ...TUBES.filter((t) => t.register === 1),
  ...TUBES.filter((t) => t.register === 2).slice(0, 2),
]
  .slice(0, 8)
  .map((t) => {
    const q = [0, 1 / 3, 2 / 3, 1].map((u) => t.curve.getPoint(u))
    const p1 = new THREE.Vector3()
      .addScaledVector(q[0], -5 / 6)
      .addScaledVector(q[1], 3)
      .addScaledVector(q[2], -3 / 2)
      .addScaledVector(q[3], 1 / 3)
    const p2 = new THREE.Vector3()
      .addScaledVector(q[0], 1 / 3)
      .addScaledVector(q[1], -3 / 2)
      .addScaledVector(q[2], 3)
      .addScaledVector(q[3], -5 / 6)
    return [q[0], p1, p2, q[3]]
  })

// Light-spill segments for the floor and the desks: the dropped run first (it is
// the visible one), then the canopy lights. 16 max (shader uniform array).
export const SPILL_SEGMENTS: { a: THREE.Vector3; b: THREE.Vector3 }[] = []
{
  const lights = TUBES.filter((t) => t.register === 2)
  const dropped = lights[lights.length - 1]
  for (let i = 0; i < 6; i++) {
    SPILL_SEGMENTS.push({ a: dropped.curve.getPoint(i / 6), b: dropped.curve.getPoint((i + 1) / 6) })
  }
  for (const t of lights.slice(0, 4)) {
    for (let i = 0; i < 3; i++) {
      if (SPILL_SEGMENTS.length >= 16) break
      SPILL_SEGMENTS.push({ a: t.curve.getPoint(i / 3), b: t.curve.getPoint((i + 1) / 3) })
    }
  }
}
